import type { Employee } from "@evolua/api-client";
import type { EmployeeDirectoryFilters } from "@evolua/schemas";
import {
  Badge,
  Button,
  EmptyState,
  FilterBar,
  FreshnessBadge,
  PageHeader,
  PartialState,
  SearchField,
  Select,
  Skeleton,
  badgeToneForStatus,
} from "@evolua/ui";
import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { flattenCursorPages } from "../../api/cursor.js";
import { QueryErrorState } from "../../api/QueryErrorState.js";
import { useEmployeeDirectory, useExportEmployees } from "../../api/use-employees.js";
import { useSession } from "../../session/SessionContext.js";
import { DataTable, type DataTableColumn } from "../data/DataTable.js";
import {
  employeeDirectoryToSearchParams,
  parseEmployeeDirectorySearch,
} from "../data/url-filters.js";

export type EmployeeDirectoryProps = {
  title: string;
  lead: string;
  caption?: string;
  /** Prefixo da rota de detalhe (ex.: /pessoas ou /equipe). */
  detailBasePath?: "/pessoas" | "/equipe";
};

export function EmployeeDirectory({
  title,
  lead,
  caption,
  detailBasePath = "/pessoas",
}: EmployeeDirectoryProps) {
  const { isDemoMode } = useSession();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseEmployeeDirectorySearch(params), [params]);
  const [searchDraft, setSearchDraft] = useState(filters.q ?? "");
  const [exportNotice, setExportNotice] = useState<string | null>(null);

  const setFilters = useCallback(
    (next: EmployeeDirectoryFilters) => {
      setParams(employeeDirectoryToSearchParams(next), { replace: true });
    },
    [setParams],
  );

  const query = useEmployeeDirectory({
    sort: filters.sort,
    limit: 25,
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.q ? { q: filters.q } : {}),
  });
  const exportMutation = useExportEmployees();
  const rows = flattenCursorPages(query.data?.pages ?? []);

  const columns: DataTableColumn<Employee>[] = useMemo(
    () => [
      {
        id: "name",
        label: "Nome",
        header: "Nome",
        sortable: true,
        cell: (row) => (
          <Link to={`${detailBasePath}/${row.id}`} className="evolua-table-link">
            {row.fullName}
          </Link>
        ),
        exportValue: (row) => row.fullName,
      },
      {
        id: "department",
        label: "Área",
        header: "Área",
        cell: (row) => row.departmentName,
        exportValue: (row) => row.departmentName,
      },
      {
        id: "position",
        label: "Cargo",
        header: "Cargo",
        cell: (row) => row.positionName ?? "—",
        exportValue: (row) => row.positionName ?? "",
      },
      {
        id: "status",
        label: "Status",
        header: "Status",
        cell: (row) => <Badge tone={badgeToneForStatus(row.status)}>{row.status}</Badge>,
        exportValue: (row) => row.status,
      },
      {
        id: "origin",
        label: "Origem",
        header: "Origem",
        cell: (row) => (
          <Badge tone={row.dataOrigin === "ERP" ? "brand" : "neutral"}>
            {row.dataOrigin ?? "—"}
          </Badge>
        ),
        exportValue: (row) => row.dataOrigin ?? "",
      },
      {
        id: "syncedAt",
        label: "Atualizado",
        header: "Atualizado",
        defaultVisible: false,
        cell: (row) =>
          row.syncedAt ? new Date(row.syncedAt).toLocaleString("pt-BR") : "—",
        exportValue: (row) => row.syncedAt ?? "",
      },
    ],
    [detailBasePath],
  );

  const freshestSyncedAt = useMemo(() => {
    const times = rows
      .map((r) => (r.syncedAt ? Date.parse(r.syncedAt) : NaN))
      .filter((t) => Number.isFinite(t));
    if (times.length === 0) {
      return query.dataUpdatedAt ? new Date(query.dataUpdatedAt) : null;
    }
    return new Date(Math.max(...times));
  }, [rows, query.dataUpdatedAt]);

  function applySearch() {
    const q = searchDraft.trim();
    setFilters({
      sort: filters.sort ?? "fullName:asc",
      ...(filters.status ? { status: filters.status } : {}),
      ...(q ? { q } : {}),
    });
  }

  function clearFilters() {
    setSearchDraft("");
    setFilters({ sort: "fullName:asc" });
  }

  return (
    <>
      <PageHeader
        title={title}
        lead={`${lead}${isDemoMode ? " (dados demo)." : " (API)."}`}
        freshness={<FreshnessBadge syncedAt={freshestSyncedAt} />}
      />

      {isDemoMode ? (
        <PartialState description="Diretório demo — origem ERP/plataforma e selo de atualização usam fixtures até o sync real (P-001)." />
      ) : null}

      {exportNotice ? (
        <PartialState title="Exportação registrada" description={exportNotice} />
      ) : null}

      <FilterBar onClear={clearFilters}>
        <SearchField
          label="Busca"
          name="q"
          value={searchDraft}
          placeholder="Nome, e-mail ou matrícula"
          onChange={(e) => setSearchDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applySearch();
            }
          }}
        />
        <Button type="button" variant="secondary" onClick={applySearch}>
          Buscar
        </Button>
        <Select
          label="Status"
          value={filters.status ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            setFilters({
              sort: filters.sort ?? "fullName:asc",
              ...(filters.q ? { q: filters.q } : {}),
              ...(value === "ACTIVE" || value === "ON_LEAVE" || value === "TERMINATED"
                ? { status: value }
                : {}),
            });
          }}
          options={[
            { value: "", label: "Todos" },
            { value: "ACTIVE", label: "Ativo" },
            { value: "ON_LEAVE", label: "Afastado" },
            { value: "TERMINATED", label: "Desligado" },
          ]}
        />
        <Select
          label="Ordenação"
          value={filters.sort ?? "fullName:asc"}
          onChange={(e) => {
            const sort = e.target.value as EmployeeDirectoryFilters["sort"];
            setFilters({
              ...(filters.status ? { status: filters.status } : {}),
              ...(filters.q ? { q: filters.q } : {}),
              sort: sort ?? "fullName:asc",
            });
          }}
          options={[
            { value: "fullName:asc", label: "Nome (A–Z)" },
            { value: "fullName:desc", label: "Nome (Z–A)" },
            { value: "hireDate:asc", label: "Admissão (antiga)" },
            { value: "hireDate:desc", label: "Admissão (recente)" },
          ]}
        />
      </FilterBar>

      {query.isPending ? (
        <div className="evolua-stack evolua-stack--2" aria-busy="true">
          <Skeleton height="2.5rem" label="Carregando colaboradores" />
          <Skeleton height="2.5rem" />
          <Skeleton height="2.5rem" />
        </div>
      ) : null}

      {query.isError ? (
        <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}

      {!query.isPending && !query.isError && rows.length === 0 ? (
        <EmptyState
          title="Nenhum colaborador encontrado"
          description="Ajuste os filtros ou aguarde a próxima sincronização com o ERP."
          action={
            <Button type="button" variant="secondary" onClick={clearFilters}>
              Limpar filtros
            </Button>
          }
        />
      ) : null}

      {!query.isPending && !query.isError && rows.length > 0 ? (
        <DataTable
          caption={caption ?? "Diretório de colaboradores"}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          emptyMessage="Nenhum colaborador encontrado"
          sort={filters.sort}
          sortField="fullName"
          onSortChange={(sort) =>
            setFilters({
              ...(filters.status ? { status: filters.status } : {}),
              ...(filters.q ? { q: filters.q } : {}),
              sort: sort as EmployeeDirectoryFilters["sort"],
            })
          }
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onLoadMore={() => void query.fetchNextPage()}
          exportBusy={exportMutation.isPending}
          exportLabel="Exportar CSV (auditado)"
          onExport={async () => {
            setExportNotice(null);
            const result = await exportMutation.mutateAsync({
              sort: filters.sort,
              ...(filters.status ? { status: filters.status } : {}),
              ...(filters.q ? { q: filters.q } : {}),
            });
            setExportNotice(
              `${result.rowCount} linha(s) · auditoria ${result.auditId || "(local)"}`,
            );
          }}
        />
      ) : null}
    </>
  );
}
